/**
 * Sidebar Component
 *
 * Main navigation sidebar for the dashboard.
 *
 * @module components/layout/Sidebar
 */

import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks';
import { links, marketingUrl } from '@/lib/config';
import { Badge } from '@/components/ui';
import {
  LayoutDashboard,
  Key,
  Settings,
  Users,
  CreditCard,
  FileText,
  SquareTerminal,
  ExternalLink,
  TrendingUp,
  Headphones,
} from 'lucide-react';
import {
  type PlanId,
  PLAN_TIERS,
  formatRequestCount,
} from '@hummingbird/pricing';

// =============================================================================
// Navigation Items
// =============================================================================

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  badge?: string;
  external?: boolean;
}

const mainNavItems: NavItem[] = [
  { label: 'Overview', to: '/overview', icon: <LayoutDashboard className="h-5 w-5" /> },
  { label: 'API Keys', to: '/keys', icon: <Key className="h-5 w-5" /> },
];

interface SettingsNavItem extends NavItem {
  /** Permission key required to see this item */
  permissionKey: 'canUpdateOrgSettings' | 'canManageTeam' | 'canAccessBilling';
}

const settingsNavItems: SettingsNavItem[] = [
  { label: 'Organization', to: '/settings/organization', icon: <Settings className="h-5 w-5" />, permissionKey: 'canUpdateOrgSettings' },
  { label: 'Team', to: '/settings/team', icon: <Users className="h-5 w-5" />, permissionKey: 'canManageTeam' },
  { label: 'Billing', to: '/settings/billing', icon: <CreditCard className="h-5 w-5" />, permissionKey: 'canAccessBilling' },
];

const resourceNavItems: NavItem[] = [
  { label: 'Documentation', to: links.docs, icon: <FileText className="h-5 w-5" />, external: true },
  { label: 'API Reference', to: marketingUrl('/docs/api/'), icon: <SquareTerminal className="h-5 w-5" />, external: true },
];

/**
 * Build contact support URL with user info pre-filled
 */
function buildContactSupportUrl(user: { displayName?: string | null; firstName?: string | null; lastName?: string | null; email?: string | null } | null): string {
  const params = new URLSearchParams({ topic: 'Support' });

  if (user?.email) {
    params.set('email', user.email);
  }

  // Build full name from firstName + lastName, or fall back to displayName
  const fullName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.displayName || '';

  if (fullName) {
    params.set('name', fullName);
  }

  return marketingUrl(`/contact/?${params.toString()}`);
}

// =============================================================================
// Nav Link Component
// =============================================================================

interface NavLinkItemProps {
  item: NavItem;
}

function NavLinkItem({ item }: NavLinkItemProps) {
  const baseStyles = `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors`;
  
  if (item.external) {
    return (
      <a
        href={item.to}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(baseStyles, 'text-gray-600 hover:bg-gray-100 hover:text-gray-900')}
      >
        {item.icon}
        <span className="flex-1">{item.label}</span>
        <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
      </a>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === '/overview'}
      className={({ isActive }) =>
        cn(
          baseStyles,
          isActive
            ? 'bg-brand-50 text-brand-700'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        )
      }
    >
      {item.icon}
      <span className="flex-1">{item.label}</span>
      {item.badge && <Badge variant="primary">{item.badge}</Badge>}
    </NavLink>
  );
}

// =============================================================================
// Sidebar Plan Indicator
// =============================================================================

function SidebarPlanIndicator() {
  const { organization } = useAuth();
  const { canAccessBilling } = usePermissions();

  const planId = (organization?.planId || 'free') as PlanId;
  const plan = PLAN_TIERS[planId];
  const isFree = planId === 'free';

  // Usage calculation
  const bucketMax = organization?.currentBucketMax;
  const usageThisPeriod = organization?.usageThisPeriod || 0;
  const lifetimeUsage = organization?.lifetimeUsage || 0;
  const usage = isFree ? lifetimeUsage : usageThisPeriod;
  const usageLimit = isFree ? 2000 : (bucketMax ?? plan.maxRequests);
  const usagePercent = Math.min((usage / usageLimit) * 100, 100);
  const isNearLimit = usagePercent >= 80;
  const isAtLimit = usagePercent >= 100;

  // Circular progress calculation
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (usagePercent / 100) * circumference;

  // Don't show the plan indicator for users without billing access
  if (!canAccessBilling) {
    return null;
  }

  return (
    <div className="p-4 border-t border-gray-200">
      <NavLink
        to="/settings/billing"
        className={cn(
          'block p-3 rounded-xl transition-all group',
          'bg-gradient-to-br from-gray-50 to-gray-100/50',
          'hover:from-gray-100 hover:to-gray-50',
          'border border-gray-200/60 hover:border-gray-300',
          'hover:shadow-sm'
        )}
      >
        <div className="flex items-center gap-3">
          {/* Circular Progress */}
          <div className="relative flex-shrink-0">
            <svg className="w-12 h-12 -rotate-90" viewBox="0 0 44 44">
              {/* Background circle */}
              <circle
                cx="22"
                cy="22"
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                className="text-gray-200"
              />
              {/* Progress circle */}
              <circle
                cx="22"
                cy="22"
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                className={cn(
                  'transition-all duration-500',
                  isAtLimit && 'text-error-500',
                  isNearLimit && !isAtLimit && 'text-warning-500',
                  !isNearLimit && 'text-brand-500'
                )}
              />
            </svg>
            {/* Center percentage or icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              {isAtLimit ? (
                <TrendingUp className="w-4 h-4 text-error-500" />
              ) : (
                <span
                  className={cn(
                    'text-[10px] font-bold tabular-nums',
                    isNearLimit ? 'text-warning-600' : 'text-gray-600'
                  )}
                >
                  {Math.round(usagePercent)}%
                </span>
              )}
            </div>
          </div>

          {/* Plan info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'text-sm font-semibold',
                  isFree ? 'text-gray-700' : 'text-brand-700'
                )}
              >
                {plan.name}
              </span>
              {!isFree && (
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                  Plan
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-1 mt-0.5">
              <span
                className={cn(
                  'text-xs font-medium tabular-nums',
                  isAtLimit && 'text-error-600',
                  isNearLimit && !isAtLimit && 'text-warning-600',
                  !isNearLimit && 'text-gray-500'
                )}
              >
                {formatRequestCount(usage)}
              </span>
              <span className="text-xs text-gray-400">/</span>
              <span className="text-xs text-gray-500 tabular-nums">
                {formatRequestCount(usageLimit)}
              </span>
            </div>
          </div>

          {/* Upgrade arrow for free users */}
          {isFree && (
            <div className="flex-shrink-0">
              <span className="text-xs font-semibold text-brand-600 group-hover:text-brand-700 transition-colors">
                Upgrade →
              </span>
            </div>
          )}
        </div>
      </NavLink>
    </div>
  );
}

// =============================================================================
// Sidebar Component
// =============================================================================

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const { user } = useAuth();
  const permissions = usePermissions();

  // Build contact support URL with user info
  const contactSupportUrl = buildContactSupportUrl(user);

  // Filter settings nav items based on permissions
  const filteredSettingsNavItems = settingsNavItems.filter(
    (item) => permissions[item.permissionKey]
  );

  return (
    <aside
      className={cn(
        'flex flex-col w-64 bg-white border-r border-gray-200',
        className
      )}
    >
      {/* Logo */}
      <div className="flex items-center gap-2 px-4 h-16 border-b border-gray-200">
        <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-brand-500 overflow-hidden">
          <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
            {/* Triple chevron - forward motion symbol */}
            <path d="M7 10L13 16L7 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
            <path d="M13 10L19 16L13 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.75"/>
            <path d="M19 10L25 16L19 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <span className="font-display font-semibold text-lg text-gray-900">
          Hummingbird
        </span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Main */}
        <div className="space-y-1">
          {mainNavItems.map((item) => (
            <NavLinkItem key={item.to} item={item} />
          ))}
        </div>

        {/* Settings - only show if user has access to any settings */}
        {filteredSettingsNavItems.length > 0 && (
          <div>
            <p className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Settings
            </p>
            <div className="space-y-1">
              {filteredSettingsNavItems.map((item) => (
                <NavLinkItem key={item.to} item={item} />
              ))}
            </div>
          </div>
        )}

        {/* Resources */}
        <div>
          <p className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Resources
          </p>
          <div className="space-y-1">
            {resourceNavItems.map((item) => (
              <NavLinkItem key={item.to} item={item} />
            ))}
            <NavLinkItem
              item={{
                label: 'Contact Support',
                to: contactSupportUrl,
                icon: <Headphones className="h-5 w-5" />,
                external: true,
              }}
            />
          </div>
        </div>
      </nav>

      {/* Plan indicator */}
      <SidebarPlanIndicator />
    </aside>
  );
}
